// ─── Type Interfaces ──────────────────────────────────────────────────────────

export interface DummySub {
  id: number;
  pair: string;
  quote: string;
  exch: string;
  icon: string | null;
  color: string;
  totalValue: number;
  totalStableProfit: number;
  totalStableProfitPct: number;
  totalAssetProfit: number;
  totalAssetProfitPct: number;
  currentStableProfit: number;
  currentStableProfitPct: number;
  currentAssetProfit: number;
  currentAssetProfitPct: number;
  priceChange: number;
  stableBalance: number;
  assetBalance: number;
  assetBalanceCoins: number;
  sidebarDelta: number;
  active: boolean;
}

export interface DummyTransaction {
  id: string;
  type: 'BUY' | 'SELL';
  pair: string;
  exch: string;
  amount: number;
  price: number;
  total: number;
  ts: string;
  bot: string;
  profit: number;
  stableValue: number;
  assetValue: number;
  subTotalValue: number;
}

export interface DummyProfitSlice {
  sym: string;
  icon: string | null;
  color: string;
  stable: number;
  asset: number;
}

export interface DummyExchange {
  name: string;
  value: number;
  color: string;
}

export interface DummyCluster {
  name: string;
  totalValue: number;
  totalChangeAbs: number;
  totalChangeRel: number;
  dayChangeAbs: number;
  dayChangeRel: number;
  avgDailyAbs: number;
  avgDailyRel: number;
  subsCount: number;
  feesPaid: number;
  stableValueAbs: number;
  stableValueRel: number;
  availableCreditsUsed: number;
  availableCreditsTotal: number;
  currentStableValueAbs: number;
  currentStableValueRel: number;
  nonAlgoDifference: number;
}

export interface DummyTotals {
  stableProfit: number;
  assetProfit: number;
  feesPaid: number;
  realized: number;
  unrealized: number;
}

// ─── Data Constants ───────────────────────────────────────────────────────────

export const dummyCluster: DummyCluster = {
  name: 'Portfolio',
  totalValue: 22759.16,
  totalChangeAbs: -4188.92,
  totalChangeRel: -0.1554,
  dayChangeAbs: 166.47,
  dayChangeRel: 0.0074,
  avgDailyAbs: -10.22,
  avgDailyRel: -0.0004,
  subsCount: 50,
  feesPaid: 242.89,
  stableValueAbs: 11284.31,
  stableValueRel: 0.4958,
  availableCreditsUsed: 34,
  availableCreditsTotal: 50,
  currentStableValueAbs: 1842.55,
  currentStableValueRel: 0.0809,
  nonAlgoDifference: -13246.32,
};

// Helper to build a sub entry concisely
function sub(
  id: number,
  pair: string,
  quote: string,
  exch: string,
  icon: string | null,
  color: string,
  totalValue: number,
  tsp: number,
  tspPct: number,
  tap: number,
  tapPct: number,
  csp: number,
  cspPct: number,
  cap: number,
  capPct: number,
  priceChange: number,
  stableBalance: number,
  assetBalance: number,
  assetBalanceCoins: number,
  sidebarDelta: number,
  active: boolean,
): DummySub {
  return {
    id,
    pair,
    quote,
    exch,
    icon,
    color,
    totalValue,
    totalStableProfit: tsp,
    totalStableProfitPct: tspPct,
    totalAssetProfit: tap,
    totalAssetProfitPct: tapPct,
    currentStableProfit: csp,
    currentStableProfitPct: cspPct,
    currentAssetProfit: cap,
    currentAssetProfitPct: capPct,
    priceChange,
    stableBalance,
    assetBalance,
    assetBalanceCoins,
    sidebarDelta,
    active,
  };
}

export const dummySubs: DummySub[] = [
  //  id  pair       quote   exch        icon          color      totalVal  tsp      tspPct   tap      tapPct   csp      cspPct   cap      capPct   priceChg  stableBal  assetBal  coins     delta   active
  sub(
    1,
    'BTC',
    'USDC',
    'Binance',
    'btc.svg',
    '#f7931a',
    1777.46,
    -32.92,
    -0.0185,
    0,
    0,
    -18.4,
    -0.0104,
    0,
    0,
    -0.0697,
    1777.46,
    0,
    0,
    0.1903,
    true,
  ),
  sub(
    2,
    'ETH',
    'USDC',
    'Binance',
    'eth.svg',
    '#627eea',
    1166.84,
    415.55,
    0.3561,
    470.21,
    0.4029,
    122.3,
    0.1048,
    88.1,
    0.0755,
    0.4029,
    0,
    1166.84,
    0.5016,
    0.0227,
    true,
  ),
  sub(
    3,
    'TAO',
    'USDC',
    'Binance',
    null,
    '#0a0a0a',
    1074.97,
    310.61,
    0.289,
    79.32,
    0.0738,
    45.2,
    0.0421,
    12.8,
    0.0119,
    0.0738,
    1074.97,
    0,
    0,
    0.0053,
    true,
  ),
  sub(
    4,
    'SOL',
    'USDC',
    'Binance',
    'sol.svg',
    '#14f195',
    974.7,
    -45.52,
    -0.0467,
    -360.3,
    -0.3696,
    -12.1,
    -0.0124,
    -42.5,
    -0.0436,
    -0.3696,
    0,
    974.7,
    11.524,
    -0.0042,
    true,
  ),
  sub(
    5,
    'SUI',
    'USDC',
    'Binance',
    'sui.svg',
    '#4ca3ff',
    903.23,
    -140.72,
    -0.1558,
    -710.05,
    -0.7861,
    -22.8,
    -0.0252,
    -98.3,
    -0.1089,
    -0.7861,
    903.23,
    0,
    0,
    0.5511,
    false,
  ),
  sub(
    6,
    'HYPE',
    'USDT',
    'Kucoin',
    null,
    '#5eead4',
    895.13,
    -24.17,
    -0.027,
    220.34,
    0.2463,
    8.4,
    0.0094,
    32.1,
    0.0359,
    0.2463,
    460,
    435.13,
    10.82,
    0.3022,
    true,
  ),
  sub(
    7,
    'AVAX',
    'USDC',
    'Binance',
    'avax.svg',
    '#e84142',
    590.0,
    -72.57,
    -0.123,
    -159.39,
    -0.2702,
    -15.2,
    -0.0258,
    -28.4,
    -0.0481,
    -0.2702,
    0,
    590.0,
    17.6,
    -0.07,
    true,
  ),
  sub(
    8,
    'PENGU',
    'USDC',
    'Binance',
    null,
    '#7c83ff',
    540.0,
    66.42,
    0.123,
    -37.8,
    -0.07,
    14.8,
    0.0274,
    -8.2,
    -0.0152,
    -0.07,
    540,
    0,
    0,
    0.04,
    true,
  ),
  sub(
    9,
    'XMR',
    'USDT',
    'Kucoin',
    'xmr.svg',
    '#ff6600',
    410.5,
    27.09,
    0.066,
    4.51,
    0.011,
    6.2,
    0.0151,
    1.8,
    0.0044,
    0.011,
    0,
    410.5,
    1.7,
    0.066,
    true,
  ),
  sub(
    10,
    'XRP',
    'USDC',
    'Binance',
    'xrp.svg',
    '#ffffff',
    820.4,
    120.1,
    0.1464,
    88.21,
    0.1075,
    28.4,
    0.0346,
    15.2,
    0.0185,
    0.0842,
    820.4,
    0,
    0,
    0.0346,
    true,
  ),
  sub(
    11,
    'BNB',
    'USDC',
    'Binance',
    'bnb.svg',
    '#f3ba2f',
    680.2,
    18.33,
    0.0269,
    45.2,
    0.0665,
    4.8,
    0.0071,
    12.3,
    0.0181,
    0.0412,
    0,
    680.2,
    1.12,
    0.0071,
    true,
  ),
  sub(
    12,
    'ADA',
    'USDC',
    'Binance',
    null,
    '#0033ad',
    450.3,
    52.1,
    0.1157,
    -28.4,
    -0.0631,
    12.8,
    0.0284,
    -5.2,
    -0.0115,
    -0.0345,
    450.3,
    0,
    0,
    0.0284,
    true,
  ),
  sub(
    13,
    'DOT',
    'USDC',
    'Binance',
    null,
    '#e6007a',
    380.15,
    -18.9,
    -0.0497,
    -42.3,
    -0.1113,
    -4.2,
    -0.011,
    -8.8,
    -0.0232,
    -0.082,
    0,
    380.15,
    55.8,
    0.015,
    true,
  ),
  sub(
    14,
    'LINK',
    'USDC',
    'Binance',
    null,
    '#2a5ada',
    520.8,
    88.42,
    0.1698,
    62.1,
    0.1192,
    22.1,
    0.0424,
    14.8,
    0.0284,
    0.0952,
    520.8,
    0,
    0,
    0.0424,
    true,
  ),
  sub(
    15,
    'MATIC',
    'USDC',
    'Binance',
    null,
    '#8247e5',
    310.2,
    -35.6,
    -0.1148,
    -88.4,
    -0.285,
    -8.4,
    -0.0271,
    -14.2,
    -0.0458,
    -0.182,
    310.2,
    0,
    0,
    -0.0271,
    false,
  ),
  sub(
    16,
    'UNI',
    'USDC',
    'Binance',
    null,
    '#ff007a',
    280.5,
    42.3,
    0.1508,
    18.9,
    0.0674,
    10.2,
    0.0364,
    4.8,
    0.0171,
    0.052,
    0,
    280.5,
    38.4,
    0.0364,
    true,
  ),
  sub(
    17,
    'AAVE',
    'USDC',
    'Binance',
    null,
    '#2ebac6',
    620.4,
    145.2,
    0.2341,
    82.1,
    0.1323,
    32.4,
    0.0522,
    18.8,
    0.0303,
    0.1128,
    620.4,
    0,
    0,
    0.0522,
    true,
  ),
  sub(
    18,
    'NEAR',
    'USDC',
    'Binance',
    null,
    '#00c08b',
    240.8,
    -12.4,
    -0.0515,
    22.8,
    0.0947,
    -2.8,
    -0.0116,
    5.4,
    0.0224,
    0.068,
    0,
    240.8,
    42.2,
    0.0115,
    true,
  ),
  sub(
    19,
    'FTM',
    'USDT',
    'Kucoin',
    null,
    '#1969ff',
    185.3,
    28.4,
    0.1533,
    -15.2,
    -0.082,
    6.8,
    0.0367,
    -3.4,
    -0.0183,
    -0.054,
    185.3,
    0,
    0,
    0.0367,
    true,
  ),
  sub(
    20,
    'ATOM',
    'USDC',
    'Binance',
    null,
    '#2e3148',
    340.6,
    -22.8,
    -0.0669,
    -54.2,
    -0.1591,
    -5.4,
    -0.0159,
    -12.8,
    -0.0376,
    -0.112,
    0,
    340.6,
    38.4,
    -0.0159,
    true,
  ),
  sub(
    21,
    'INJ',
    'USDT',
    'Kucoin',
    null,
    '#0082ff',
    420.2,
    92.3,
    0.2196,
    48.4,
    0.1152,
    18.4,
    0.0438,
    10.2,
    0.0243,
    0.088,
    420.2,
    0,
    0,
    0.0438,
    true,
  ),
  sub(
    22,
    'OP',
    'USDC',
    'Binance',
    null,
    '#ff0420',
    290.4,
    -8.2,
    -0.0282,
    32.4,
    0.1116,
    -1.8,
    -0.0062,
    8.2,
    0.0282,
    0.084,
    0,
    290.4,
    188.2,
    0.0124,
    true,
  ),
  sub(
    23,
    'ARB',
    'USDC',
    'Binance',
    null,
    '#213147',
    260.8,
    35.4,
    0.1357,
    -22.1,
    -0.0847,
    8.2,
    0.0314,
    -4.8,
    -0.0184,
    -0.052,
    260.8,
    0,
    0,
    0.0314,
    true,
  ),
  sub(
    24,
    'DOGE',
    'USDC',
    'Binance',
    null,
    '#c2a633',
    480.2,
    -58.4,
    -0.1216,
    120.3,
    0.2505,
    -12.4,
    -0.0258,
    28.4,
    0.0591,
    0.184,
    0,
    480.2,
    2842,
    0.0124,
    true,
  ),
  sub(
    25,
    'SHIB',
    'USDT',
    'Binance',
    null,
    '#ffa409',
    120.4,
    18.2,
    0.1512,
    -8.4,
    -0.0698,
    4.2,
    0.0349,
    -1.8,
    -0.015,
    -0.042,
    120.4,
    0,
    0,
    0.0349,
    false,
  ),
  sub(
    26,
    'LTC',
    'USDC',
    'Coinbase',
    null,
    '#bfbbbb',
    380.6,
    42.8,
    0.1125,
    28.4,
    0.0746,
    10.2,
    0.0268,
    6.8,
    0.0179,
    0.058,
    380.6,
    0,
    0,
    0.0268,
    true,
  ),
  sub(
    27,
    'ALGO',
    'USDC',
    'Coinbase',
    null,
    '#000000',
    150.2,
    -22.4,
    -0.1491,
    -38.2,
    -0.2543,
    -5.2,
    -0.0346,
    -8.4,
    -0.0559,
    -0.184,
    150.2,
    0,
    0,
    -0.0346,
    false,
  ),
  sub(
    28,
    'FIL',
    'USDC',
    'Binance',
    null,
    '#0090ff',
    210.4,
    12.8,
    0.0608,
    -18.4,
    -0.0874,
    2.8,
    0.0133,
    -4.2,
    -0.02,
    -0.058,
    0,
    210.4,
    42.8,
    0.0133,
    true,
  ),
  sub(
    29,
    'RENDER',
    'USDC',
    'Binance',
    null,
    '#000000',
    340.8,
    82.4,
    0.2418,
    55.2,
    0.1619,
    18.2,
    0.0534,
    12.4,
    0.0364,
    0.124,
    340.8,
    0,
    0,
    0.0534,
    true,
  ),
  sub(
    30,
    'TIA',
    'USDT',
    'Kucoin',
    null,
    '#7b2bf9',
    280.2,
    -15.4,
    -0.055,
    42.8,
    0.1527,
    -3.2,
    -0.0114,
    10.2,
    0.0364,
    0.118,
    0,
    280.2,
    28.4,
    0.0124,
    true,
  ),
  sub(
    31,
    'SEI',
    'USDC',
    'Binance',
    null,
    '#9b1c2e',
    190.4,
    28.8,
    0.1512,
    -12.4,
    -0.0651,
    6.8,
    0.0357,
    -2.8,
    -0.0147,
    -0.038,
    190.4,
    0,
    0,
    0.0357,
    true,
  ),
  sub(
    32,
    'APT',
    'USDC',
    'Binance',
    null,
    '#2ed8a3',
    420.8,
    -42.2,
    -0.1003,
    28.4,
    0.0675,
    -8.8,
    -0.0209,
    6.2,
    0.0147,
    0.042,
    0,
    420.8,
    48.2,
    -0.0209,
    true,
  ),
  sub(
    33,
    'WLD',
    'USDT',
    'MEXC',
    null,
    '#000000',
    160.2,
    22.4,
    0.1398,
    -28.8,
    -0.1798,
    5.2,
    0.0325,
    -6.4,
    -0.04,
    -0.112,
    160.2,
    0,
    0,
    0.0325,
    true,
  ),
  sub(
    34,
    'PEPE',
    'USDT',
    'Binance',
    null,
    '#479f47',
    220.4,
    -35.2,
    -0.1597,
    85.4,
    0.3875,
    -7.8,
    -0.0354,
    18.2,
    0.0826,
    0.284,
    0,
    220.4,
    18420000,
    0.0124,
    true,
  ),
  sub(
    35,
    'FET',
    'USDC',
    'Binance',
    null,
    '#1d1d3c',
    310.2,
    55.4,
    0.1786,
    32.8,
    0.1057,
    12.4,
    0.04,
    7.8,
    0.0251,
    0.078,
    310.2,
    0,
    0,
    0.04,
    true,
  ),
  sub(
    36,
    'GRT',
    'USDC',
    'Coinbase',
    null,
    '#6747ed',
    130.8,
    -8.2,
    -0.0627,
    -22.4,
    -0.1712,
    -1.8,
    -0.0138,
    -5.2,
    -0.0397,
    -0.128,
    0,
    130.8,
    582,
    -0.0138,
    true,
  ),
  sub(
    37,
    'RUNE',
    'USDC',
    'Binance',
    null,
    '#33ff99',
    280.4,
    42.8,
    0.1527,
    18.2,
    0.0649,
    10.2,
    0.0364,
    4.8,
    0.0171,
    0.048,
    280.4,
    0,
    0,
    0.0364,
    true,
  ),
  sub(
    38,
    'STX',
    'USDC',
    'Binance',
    null,
    '#5546ff',
    190.8,
    -12.4,
    -0.065,
    28.8,
    0.1509,
    -2.8,
    -0.0147,
    6.8,
    0.0356,
    0.112,
    0,
    190.8,
    98.4,
    0.0124,
    true,
  ),
  sub(
    39,
    'IMX',
    'USDT',
    'MEXC',
    null,
    '#04b3e8',
    150.4,
    18.8,
    0.125,
    -8.2,
    -0.0545,
    4.2,
    0.0279,
    -1.8,
    -0.012,
    -0.034,
    150.4,
    0,
    0,
    0.0279,
    false,
  ),
  sub(
    40,
    'CRV',
    'USDC',
    'Binance',
    null,
    '#a4a4f4',
    88.2,
    -15.8,
    -0.1791,
    -28.4,
    -0.322,
    -3.4,
    -0.0386,
    -6.2,
    -0.0703,
    -0.234,
    88.2,
    0,
    0,
    -0.0386,
    true,
  ),
  sub(
    41,
    'MKR',
    'USDC',
    'Coinbase',
    null,
    '#1aab9b',
    580.2,
    125.4,
    0.2161,
    68.2,
    0.1175,
    28.8,
    0.0496,
    15.2,
    0.0262,
    0.092,
    0,
    580.2,
    0.32,
    0.0496,
    true,
  ),
  sub(
    42,
    'SAND',
    'USDC',
    'Binance',
    null,
    '#04adef',
    95.4,
    -18.2,
    -0.1907,
    -32.8,
    -0.3438,
    -4.2,
    -0.044,
    -7.2,
    -0.0755,
    -0.248,
    95.4,
    0,
    0,
    -0.044,
    false,
  ),
  sub(
    43,
    'MANA',
    'USDC',
    'Binance',
    null,
    '#ff2d55',
    78.8,
    8.4,
    0.1066,
    -15.2,
    -0.1929,
    1.8,
    0.0228,
    -3.4,
    -0.0431,
    -0.138,
    0,
    78.8,
    182,
    0.0228,
    true,
  ),
  sub(
    44,
    'EGLD',
    'USDT',
    'MEXC',
    null,
    '#000000',
    320.4,
    48.2,
    0.1505,
    22.8,
    0.0712,
    10.8,
    0.0337,
    5.4,
    0.0169,
    0.052,
    320.4,
    0,
    0,
    0.0337,
    true,
  ),
  sub(
    45,
    'ICP',
    'USDC',
    'Coinbase',
    null,
    '#29abe2',
    260.8,
    -28.4,
    -0.1089,
    42.2,
    0.1618,
    -6.2,
    -0.0238,
    10.2,
    0.0391,
    0.124,
    0,
    260.8,
    22.4,
    -0.0238,
    true,
  ),
  sub(
    46,
    'ONDO',
    'USDT',
    'Kucoin',
    null,
    '#1a2b6b',
    380.2,
    72.4,
    0.1904,
    -18.2,
    -0.0479,
    16.2,
    0.0426,
    -4.2,
    -0.011,
    -0.028,
    380.2,
    0,
    0,
    0.0426,
    true,
  ),
  sub(
    47,
    'KAS',
    'USDT',
    'MEXC',
    null,
    '#49eacb',
    220.4,
    35.8,
    0.1624,
    52.4,
    0.2378,
    8.2,
    0.0372,
    12.4,
    0.0563,
    0.184,
    0,
    220.4,
    1842,
    0.0372,
    true,
  ),
  sub(
    48,
    'BONK',
    'USDT',
    'Binance',
    null,
    '#f19e39',
    65.2,
    -12.4,
    -0.1902,
    28.4,
    0.4356,
    -2.8,
    -0.0429,
    6.4,
    0.0982,
    0.324,
    65.2,
    0,
    0,
    -0.0429,
    true,
  ),
  sub(
    49,
    'WIF',
    'USDT',
    'Binance',
    null,
    '#e8b931',
    140.8,
    22.4,
    0.159,
    -42.8,
    -0.304,
    5.2,
    0.0369,
    -9.8,
    -0.0696,
    -0.212,
    140.8,
    0,
    0,
    0.0369,
    false,
  ),
  sub(
    50,
    'JUP',
    'USDT',
    'Kucoin',
    null,
    '#0fd5a5',
    180.2,
    -8.8,
    -0.0488,
    32.4,
    0.1798,
    -1.8,
    -0.01,
    7.8,
    0.0433,
    0.138,
    0,
    180.2,
    228,
    0.0124,
    true,
  ),
];

export const dummyProfitSlices: DummyProfitSlice[] = [
  { sym: 'BTC', icon: 'btc.svg', color: '#f7931a', stable: -32.92, asset: 0 },
  {
    sym: 'ETH',
    icon: 'eth.svg',
    color: '#627eea',
    stable: 415.55,
    asset: 470.21,
  },
  { sym: 'TAO', icon: null, color: '#ffffff', stable: 310.61, asset: 79.32 },
  {
    sym: 'SOL',
    icon: 'sol.svg',
    color: '#14f195',
    stable: -45.52,
    asset: -360.3,
  },
  {
    sym: 'SUI',
    icon: 'sui.svg',
    color: '#4ca3ff',
    stable: -140.72,
    asset: -710.05,
  },
  { sym: 'HYPE', icon: null, color: '#5eead4', stable: -24.17, asset: 220.34 },
  {
    sym: 'AVAX',
    icon: 'avax.svg',
    color: '#e84142',
    stable: -72.57,
    asset: -159.39,
  },
  { sym: 'PENGU', icon: null, color: '#7c83ff', stable: 66.42, asset: -37.8 },
  { sym: 'XMR', icon: 'xmr.svg', color: '#ff6600', stable: 27.09, asset: 4.51 },
  {
    sym: 'XRP',
    icon: 'xrp.svg',
    color: '#ffffff',
    stable: 120.1,
    asset: 88.21,
  },
  { sym: 'BNB', icon: 'bnb.svg', color: '#f3ba2f', stable: 18.33, asset: 45.2 },
  { sym: 'ADA', icon: null, color: '#0033ad', stable: 52.1, asset: -28.4 },
  { sym: 'DOT', icon: null, color: '#e6007a', stable: -18.9, asset: -42.3 },
  { sym: 'LINK', icon: null, color: '#2a5ada', stable: 88.42, asset: 62.1 },
  { sym: 'DOGE', icon: null, color: '#c2a633', stable: -58.4, asset: 120.3 },
  { sym: 'AAVE', icon: null, color: '#2ebac6', stable: 145.2, asset: 82.1 },
];

export const dummyTransactions: DummyTransaction[] = [
  {
    id: 't1',
    type: 'BUY',
    pair: 'ETH/USDC',
    exch: 'Binance',
    amount: 0.0421,
    price: 3142.1,
    total: 132.28,
    ts: '19 Apr 2026 08:12',
    bot: 'core',
    profit: 4.82,
    stableValue: 0,
    assetValue: 132.28,
    subTotalValue: 1166.84,
  },
  {
    id: 't2',
    type: 'SELL',
    pair: 'SOL/USDC',
    exch: 'Binance',
    amount: 1.25,
    price: 84.55,
    total: 105.69,
    ts: '19 Apr 2026 06:48',
    bot: 'core',
    profit: -8.42,
    stableValue: 105.69,
    assetValue: 0,
    subTotalValue: 974.7,
  },
  {
    id: 't3',
    type: 'BUY',
    pair: 'TAO/USDC',
    exch: 'Binance',
    amount: 0.48,
    price: 312.4,
    total: 149.95,
    ts: '19 Apr 2026 03:02',
    bot: 'rebalance',
    profit: 12.4,
    stableValue: 0,
    assetValue: 149.95,
    subTotalValue: 1074.97,
  },
  {
    id: 't4',
    type: 'SELL',
    pair: 'BTC/USDC',
    exch: 'Binance',
    amount: 0.0019,
    price: 63420.0,
    total: 120.5,
    ts: '18 Apr 2026 22:10',
    bot: 'core',
    profit: 2.18,
    stableValue: 120.5,
    assetValue: 0,
    subTotalValue: 1777.46,
  },
  {
    id: 't5',
    type: 'BUY',
    pair: 'HYPE/USDT',
    exch: 'Kucoin',
    amount: 41.2,
    price: 8.14,
    total: 335.37,
    ts: '18 Apr 2026 19:55',
    bot: 'momentum',
    profit: -5.2,
    stableValue: 0,
    assetValue: 335.37,
    subTotalValue: 895.13,
  },
  {
    id: 't6',
    type: 'BUY',
    pair: 'SUI/USDC',
    exch: 'Binance',
    amount: 88.1,
    price: 1.241,
    total: 109.37,
    ts: '18 Apr 2026 14:21',
    bot: 'rebalance',
    profit: -22.8,
    stableValue: 0,
    assetValue: 109.37,
    subTotalValue: 903.23,
  },
  {
    id: 't7',
    type: 'SELL',
    pair: 'AVAX/USDC',
    exch: 'Binance',
    amount: 4.8,
    price: 24.31,
    total: 116.69,
    ts: '18 Apr 2026 11:02',
    bot: 'core',
    profit: -15.2,
    stableValue: 116.69,
    assetValue: 0,
    subTotalValue: 590.0,
  },
  {
    id: 't8',
    type: 'BUY',
    pair: 'XMR/USDT',
    exch: 'Kucoin',
    amount: 0.214,
    price: 161.2,
    total: 34.5,
    ts: '18 Apr 2026 09:40',
    bot: 'core',
    profit: 1.8,
    stableValue: 0,
    assetValue: 34.5,
    subTotalValue: 410.5,
  },
  {
    id: 't9',
    type: 'BUY',
    pair: 'PENGU/USDC',
    exch: 'Binance',
    amount: 6420,
    price: 0.04122,
    total: 264.63,
    ts: '17 Apr 2026 23:11',
    bot: 'momentum',
    profit: 14.8,
    stableValue: 0,
    assetValue: 264.63,
    subTotalValue: 540.0,
  },
  {
    id: 't10',
    type: 'SELL',
    pair: 'ETH/USDC',
    exch: 'Binance',
    amount: 0.021,
    price: 3098.2,
    total: 65.06,
    ts: '17 Apr 2026 17:28',
    bot: 'core',
    profit: 8.4,
    stableValue: 65.06,
    assetValue: 0,
    subTotalValue: 1166.84,
  },
  {
    id: 't11',
    type: 'BUY',
    pair: 'BTC/USDC',
    exch: 'Binance',
    amount: 0.0014,
    price: 62980.0,
    total: 88.17,
    ts: '17 Apr 2026 12:02',
    bot: 'rebalance',
    profit: -3.2,
    stableValue: 0,
    assetValue: 88.17,
    subTotalValue: 1777.46,
  },
  {
    id: 't12',
    type: 'SELL',
    pair: 'SOL/USDC',
    exch: 'Binance',
    amount: 0.91,
    price: 86.2,
    total: 78.44,
    ts: '17 Apr 2026 09:16',
    bot: 'core',
    profit: -12.4,
    stableValue: 78.44,
    assetValue: 0,
    subTotalValue: 974.7,
  },
  {
    id: 't13',
    type: 'BUY',
    pair: 'LINK/USDC',
    exch: 'Binance',
    amount: 8.2,
    price: 14.82,
    total: 121.52,
    ts: '16 Apr 2026 22:40',
    bot: 'core',
    profit: 22.1,
    stableValue: 0,
    assetValue: 121.52,
    subTotalValue: 520.8,
  },
  {
    id: 't14',
    type: 'SELL',
    pair: 'DOGE/USDC',
    exch: 'Binance',
    amount: 1420,
    price: 0.169,
    total: 239.98,
    ts: '16 Apr 2026 18:15',
    bot: 'momentum',
    profit: -12.4,
    stableValue: 239.98,
    assetValue: 0,
    subTotalValue: 480.2,
  },
  {
    id: 't15',
    type: 'BUY',
    pair: 'AAVE/USDC',
    exch: 'Binance',
    amount: 0.42,
    price: 268.4,
    total: 112.73,
    ts: '16 Apr 2026 14:30',
    bot: 'core',
    profit: 32.4,
    stableValue: 0,
    assetValue: 112.73,
    subTotalValue: 620.4,
  },
  {
    id: 't16',
    type: 'SELL',
    pair: 'ADA/USDC',
    exch: 'Binance',
    amount: 320,
    price: 0.445,
    total: 142.4,
    ts: '16 Apr 2026 10:55',
    bot: 'rebalance',
    profit: 12.8,
    stableValue: 142.4,
    assetValue: 0,
    subTotalValue: 450.3,
  },
  {
    id: 't17',
    type: 'BUY',
    pair: 'INJ/USDT',
    exch: 'Kucoin',
    amount: 5.8,
    price: 22.4,
    total: 129.92,
    ts: '15 Apr 2026 21:20',
    bot: 'core',
    profit: 18.4,
    stableValue: 0,
    assetValue: 129.92,
    subTotalValue: 420.2,
  },
  {
    id: 't18',
    type: 'SELL',
    pair: 'DOT/USDC',
    exch: 'Binance',
    amount: 12.4,
    price: 6.82,
    total: 84.57,
    ts: '15 Apr 2026 16:45',
    bot: 'core',
    profit: -4.2,
    stableValue: 84.57,
    assetValue: 0,
    subTotalValue: 380.15,
  },
  {
    id: 't19',
    type: 'BUY',
    pair: 'RENDER/USDC',
    exch: 'Binance',
    amount: 18.2,
    price: 7.84,
    total: 142.69,
    ts: '15 Apr 2026 11:30',
    bot: 'momentum',
    profit: 18.2,
    stableValue: 0,
    assetValue: 142.69,
    subTotalValue: 340.8,
  },
  {
    id: 't20',
    type: 'SELL',
    pair: 'XRP/USDC',
    exch: 'Binance',
    amount: 180,
    price: 0.622,
    total: 111.96,
    ts: '14 Apr 2026 20:10',
    bot: 'core',
    profit: 28.4,
    stableValue: 111.96,
    assetValue: 0,
    subTotalValue: 820.4,
  },
  {
    id: 't21',
    type: 'BUY',
    pair: 'BNB/USDC',
    exch: 'Binance',
    amount: 0.18,
    price: 605.2,
    total: 108.94,
    ts: '14 Apr 2026 15:22',
    bot: 'rebalance',
    profit: 4.8,
    stableValue: 0,
    assetValue: 108.94,
    subTotalValue: 680.2,
  },
  {
    id: 't22',
    type: 'SELL',
    pair: 'FTM/USDT',
    exch: 'Kucoin',
    amount: 420,
    price: 0.882,
    total: 370.44,
    ts: '14 Apr 2026 08:40',
    bot: 'core',
    profit: 6.8,
    stableValue: 370.44,
    assetValue: 0,
    subTotalValue: 185.3,
  },
  {
    id: 't23',
    type: 'BUY',
    pair: 'TIA/USDT',
    exch: 'Kucoin',
    amount: 8.4,
    price: 9.12,
    total: 76.61,
    ts: '13 Apr 2026 19:55',
    bot: 'momentum',
    profit: -3.2,
    stableValue: 0,
    assetValue: 76.61,
    subTotalValue: 280.2,
  },
  {
    id: 't24',
    type: 'SELL',
    pair: 'MKR/USDC',
    exch: 'Coinbase',
    amount: 0.04,
    price: 2842.0,
    total: 113.68,
    ts: '13 Apr 2026 14:30',
    bot: 'core',
    profit: 28.8,
    stableValue: 113.68,
    assetValue: 0,
    subTotalValue: 580.2,
  },
  {
    id: 't25',
    type: 'BUY',
    pair: 'KAS/USDT',
    exch: 'MEXC',
    amount: 840,
    price: 0.128,
    total: 107.52,
    ts: '13 Apr 2026 09:10',
    bot: 'core',
    profit: 8.2,
    stableValue: 0,
    assetValue: 107.52,
    subTotalValue: 220.4,
  },
];

export const dummyTotals: DummyTotals = {
  stableProfit: 642.2,
  assetProfit: -359.75,
  feesPaid: 242.89,
  realized: 1240.55,
  unrealized: -842.3,
};

const balanceSeed = [
  0.4, -1.2, 0.6, -0.3, 1.1, -0.9, -0.4, 1.4, -0.8, -1.6, 0.2, -2.1, 0.7, -1.0,
  -0.5, 1.2, -0.6, -1.4, 0.3, -0.9, -1.7, 0.4, -0.2, -1.1, -0.6, 1.3, -2.0,
  -0.7, 0.5, -1.3, -0.8, -0.4, 1.0, -1.5, -0.9, 0.2, -1.0, -0.5, -0.7, 1.1,
  -1.6, -0.3, -1.0, 0.4, -0.6, -1.2, -0.4, 0.8, -0.5, -1.0, -1.3, 0.6, -0.7,
  -0.4, -0.9, 0.5, -1.1, -0.6, 0.3, -0.5,
];

export const dummyBalanceSeries: number[] = (() => {
  const series: number[] = [];
  let current = 26948;
  for (let i = 0; i < 60; i++) {
    series.push(current);
    current += balanceSeed[i] * 180;
  }
  return series;
})();

export const dummyPassiveSeries: number[] = (() => {
  const series: number[] = [];
  let current = 26948;
  for (let i = 0; i < 60; i++) {
    series.push(current);
    current -= 60 + i * 1.6;
  }
  return series;
})();

export const dummyProfitSeries: number[] = dummyBalanceSeries.map(
  (v, i) => v - dummyPassiveSeries[i],
);

export const dummyExchanges: DummyExchange[] = [
  { name: 'Binance', value: 17893.21, color: '#f3ba2f' },
  { name: 'Kucoin', value: 3450.4, color: '#26d4a8' },
  { name: 'MEXC', value: 980.5, color: '#3aafff' },
  { name: 'Coinbase', value: 435.05, color: '#1652f0' },
];

// ─── Utility Functions ────────────────────────────────────────────────────────

export function formatNumEU(n: number): string {
  const formatted = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatted.replace('.', '§').replace(/,/g, '.').replace('§', ',');
}

export function formatPct(n: number): string {
  return (n > 0 ? '+' : '') + (n * 100).toFixed(2).replace('.', ',') + '%';
}

export function signedNum(n: number): string {
  return (n >= 0 ? '+' : '-') + '$' + formatNumEU(Math.abs(n));
}

export function getTimeframeLabel(tf: string): string {
  const map: Record<string, string> = {
    '1W': 'since last week',
    '1M': 'since last month',
    '2M': 'since 2 months ago',
    '3M': 'since 3 months ago',
    '6M': 'since 6 months ago',
    '1Y': 'since last year',
    '2Y': 'since 2 years ago',
    ALL: 'since inception',
  };
  return map[tf] ?? tf;
}
